import axios from 'axios';
import { API_BASE_URL, WEB_API_BASE_URL } from '../config/config';
import { handleSessionExpired } from '../utils/sessionExpiredHandler';

export interface FieldOption {
  value: string;
  text: string;
}

export interface FormField {
  fieldName: string;
  label: string;
  fieldType: string;
  isVisible: boolean;
  isMandatory: boolean;
  isReadOnly: boolean;
  isEditable: boolean;
  displayOrder: number;
  section: string;
  columnSpan?: number;
  sourceType?: string;
  dataSource?: string;
  displayMember?: string;
  valueMember?: string;
  validationPattern?: string;
  minLength?: number;
  maxLength?: number;
  minValue?: number;
  maxValue?: number;
  isEmail?: boolean;
  errorMessage?: string;
  placeholder?: string;
  helpText?: string;
  defaultValue?: any;
  dependsOn?: string;
  staticOptions?: FieldOption[] | string;
  apiMethod?: string;
  apiHeaders?: string;
  isLocationField?: boolean;
  maxFileSize?: number;
  allowedFileTypes?: string;

  // TextBoxWithSearch and Auto-population fields
  onChangeApiEndpoint1?: string;
  onChangeApiEndpoint2?: string;
  onChangeApiEndpoint3?: string;
  onChangeApiEndpoint4?: string;
  onChangeApiEndpoint5?: string;
  onChangeApiCount?: number;
  isOnChangeFetchDataFromAPI?: boolean;

  // Auto-population configuration
  isApiPopulatedColumn?: boolean;
  apiPopulatedColumnParentFieldId?: string;
  apiPopulatedColumnParentFieldIdPropertyName?: string;
  apiNumber?: number;

  // Parent field visibility configuration
  parentFieldId?: string;
  parentFieldValue?: string;
  conditionalLogicOperator?: string;
  secondaryParentFieldId?: string;
  secondaryParentFieldValue?: string;
  conditionOperator?: string;

  // Repeating Section Configuration
  isRepeatingSection?: boolean;
  repeatingSectionId?: string;
  repeatingSectionName?: string;
  repeatingTriggerType?: 'quantity' | 'manual' | 'api_response' | 'self';
  repeatingTriggerFieldId?: string;
  repeatingSectionApiEndpoint?: string;
  repeatingMinRows?: number;
  repeatingMaxRows?: number;
  repeatingAllowAddRemove?: boolean;
  repeatingLayout?: 'table' | 'card';
  repeatingShowRowNumbers?: boolean;
  repeatingShowDeleteButton?: boolean;
  repeatingRowTitleTemplate?: string;
  
  // Nested Table Configuration
  repeatingParentSectionId?: string;
  repeatingChildSectionIds?: string[];
  repeatingNestedLevel?: number;
  repeatingAllowNesting?: boolean;
  
  // Table metadata
  tableName?: string;
  tableColumnName?: string;
  tableType?: string;
}

export interface FormConfiguration {
  fields: FormField[];
  sections: Map<string, FormField[]>;
}

class FormConfigService {
  private apiClient = axios.create({
    baseURL: API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  private webApiClient = axios.create({
    baseURL: WEB_API_BASE_URL,
    timeout: 30000,
    headers: {
      'Content-Type': 'application/json',
    },
  });

  constructor() {
    this.apiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    this.webApiClient.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => Promise.reject(error)
    );

    // 401 handler for both clients
    const handle401 = (error: any) => {
      if (error.response?.status === 401) {
        handleSessionExpired();
      }
      return Promise.reject(error);
    };
    this.apiClient.interceptors.response.use((r) => r, handle401);
    this.webApiClient.interceptors.response.use((r) => r, handle401);
  }

  /**
   * Fetch form configuration from WebAPI (not MobileService)
   */
  async getFormConfiguration(entityName: string, tableType: string = 'header'): Promise<FormConfiguration> {
    try {
      // Get required parameters
      const roleTypeUID = localStorage.getItem('roleTypeUID') || '0dcae222-5f10-4452-a15a-50280be4c0ce';
      const fromStatus = ''; // Empty for add mode

      console.log('Fetching form configuration from WebAPI:', {
        entityName,
        roleTypeUID,
        fromStatus
      });

      // Call WebAPI endpoint to get approval matrix configuration
      // Using same endpoint as Angular UI: ApprovalMatrix/GetApprovalMatrixByFormName
      const response = await this.webApiClient.get(
        `/ApprovalMatrix/GetApprovalMatrixByFormName?FormName=${encodeURIComponent(entityName)}&RoleTypeUID=${roleTypeUID}&FromStatus=${fromStatus}`
      );

      console.log('WebAPI Response:', response.data);

      if (!response.data.Success && !response.data.success) {
        throw new Error(response.data.Message || response.data.message || 'Failed to fetch form configuration');
      }

      const data = response.data.Data || response.data.data || [];
      console.log('Form fields received:', data.length);

      const fields = this.parseFormFields(data);
      const sections = this.groupFieldsBySection(fields);

      return {
        fields,
        sections
      };
    } catch (error: any) {
      console.error('Error fetching form configuration from WebAPI:', error);
      throw new Error(error.response?.data?.Message || error.response?.data?.message || 'Failed to fetch form configuration');
    }
  }

  /**
   * Parse form fields from API response
   */
  private parseFormFields(data: any[]): FormField[] {
    return data.map(field => {
      // Debug logging for TextBoxWithSearch fields
      if (field.UI_Input_Type === 'TextBoxWithSearch' || field.fieldType === 'TextBoxWithSearch') {
        console.log('=== TextBoxWithSearch Field Debug ===');
        console.log('Field:', field.UI_Field_Text || field.label);
        console.log('All field keys:', Object.keys(field));
        console.log('On_ChangeAPI_EndPoint_1:', field.On_ChangeAPI_EndPoint_1);
        console.log('On_ChangeAPI_Endpoint_1:', field.On_ChangeAPI_Endpoint_1);
        console.log('OnChangeAPIEndPoint1:', field['OnChangeAPIEndPoint1']);
        console.log('onChangeApiEndpoint1:', field.onChangeApiEndpoint1);
        console.log('Is_OnChange_FetchData_From_API:', field.Is_OnChange_FetchData_From_API);
        console.log('On_Change_API_Count:', field.On_Change_API_Count);

        // Check all possible variations of the API endpoint property
        const possibleKeys = [
          'On_ChangeAPI_EndPoint_1',
          'On_ChangeAPI_Endpoint_1',
          'OnChangeAPIEndPoint1',
          'onChangeAPIEndPoint1',
          'onChangeApiEndpoint1',
          'On_Change_API_EndPoint_1',
          'apiEndpoint1',
          'OnChangeApiEndpoint1'
        ];
        console.log('Checking all possible API endpoint keys:');
        possibleKeys.forEach(key => {
          if (field[key]) {
            console.log(`  ✓ Found: ${key} =`, field[key]);
          }
        });

        console.log('Full field object:', field);
      }

      // Parse static options if they're a JSON string
      let staticOptions = field.staticOptions;
      if (typeof staticOptions === 'string' && staticOptions) {
        try {
          staticOptions = JSON.parse(staticOptions);
        } catch (e) {
          console.error('Error parsing static options:', e);
          staticOptions = [];
        }
      }

      // Normalize field type from database naming to component naming
      let fieldType = field.UI_Input_Type || field.fieldType || 'text';
      const fieldTypeMapping: Record<string, string> = {
        'BarcodeScanner': 'qrscanner',
        'DateTime': 'datetime-local',
        'DropDown': 'dropdown',
        'DropDownPopUp': 'dropdownpopup',
        'GeoLocationCapture': 'location',
        'MultiSelect': 'multiselect',
        'TextBox': 'text',
        'TextBoxWithSearch': 'TextBoxWithSearch',
        'H2': 'H2',
        'Camera': 'camera',
        'Image Uploader': 'Image Uploader',
        'Image Gallery': 'Image Gallery',
        // Keep original casing for these
        'textboxwithsearch': 'TextBoxWithSearch',
        'h2': 'H2'
      };
      fieldType = fieldTypeMapping[fieldType] || fieldType.toLowerCase();

      return {
        ...field,
        fieldName: field.UI_Field_Id || field.fieldName || field.Table_Column_Name,
        label: field.UI_Field_Text || field.label || field.UI_Field_Id,
        section: field.UI_Main_Tab || field.section || 'General',
        displayOrder: field.UI_Field_Order || field.displayOrder || 0,
        placeholder: field.placeholder || field.UI_Field_Text,
        fieldType,
        staticOptions,
        isVisible: field.isVisible === true || field.isVisible === 1 || field.UI_Field_IsVisible === true || field.UI_Field_IsVisible === 1,
        isMandatory: field.isMandatory === true || field.isMandatory === 1 || field.UI_Field_IsMandatory === true || field.UI_Field_IsMandatory === 1,
        isReadOnly: field.isReadOnly === true || field.isReadOnly === 1,
        isEditable: field.isEditable === true || field.isEditable === 1 || field.UI_Field_IsEnabled === true || field.UI_Field_IsEnabled === 1,
        isLocationField: field.isLocationField === true || field.isLocationField === 1,
        sourceType: field.Field_Options_Source || field.sourceType || 'static',
        dataSource: field.Field_Options_API_Endpoint || field.dataSource,

        // TextBoxWithSearch and Auto-population fields
        // Note: Database uses On_ChangeAPI_EndPoint (capital P) not On_ChangeAPI_Endpoint
        onChangeApiEndpoint1: field.On_ChangeAPI_EndPoint_1 || field.On_ChangeAPI_Endpoint_1 || field.onChangeApiEndpoint1,
        onChangeApiEndpoint2: field.On_ChangeAPI_EndPoint_2 || field.On_ChangeAPI_Endpoint_2 || field.onChangeApiEndpoint2,
        onChangeApiEndpoint3: field.On_ChangeAPI_EndPoint_3 || field.On_ChangeAPI_Endpoint_3 || field.onChangeApiEndpoint3,
        onChangeApiEndpoint4: field.On_ChangeAPI_EndPoint_4 || field.On_ChangeAPI_Endpoint_4 || field.onChangeApiEndpoint4,
        onChangeApiEndpoint5: field.On_ChangeAPI_EndPoint_5 || field.On_ChangeAPI_Endpoint_5 || field.onChangeApiEndpoint5,
        onChangeApiCount: field.On_Change_API_Count || field.onChangeApiCount || 1,
        isOnChangeFetchDataFromAPI: field.Is_OnChange_FetchData_From_API === true || field.Is_OnChange_FetchData_From_API === 1,

        // Auto-population configuration
        isApiPopulatedColumn: field.Is_API_Populated_Column === true || field.Is_API_Populated_Column === 1,
        apiPopulatedColumnParentFieldId: field.API_Populated_Column_Parent_Field_Id || field.apiPopulatedColumnParentFieldId,
        apiPopulatedColumnParentFieldIdPropertyName: field.API_Populated_Column_Parent_Field_Id_PropertyName || field.apiPopulatedColumnParentFieldIdPropertyName,
        apiNumber: field.API_Number || field.apiNumber || 1,

        // Parent field visibility configuration
        parentFieldId: field.UI_Parent_Field_Id || field.parentFieldId,
        parentFieldValue: field.UI_Parent_Field_Value || field.parentFieldValue,
        conditionalLogicOperator: field.Conditional_Logic_Operator || field.conditionalLogicOperator,
        secondaryParentFieldId: field.Secondary_Parent_Field_Id || field.secondaryParentFieldId,
        secondaryParentFieldValue: field.Secondary_Parent_Field_Value || field.secondaryParentFieldValue,
        conditionOperator: field.Condition_Operator || field.conditionOperator || 'equals',
      };
    }).sort((a, b) => a.displayOrder - b.displayOrder);
  }

  /**
   * Group fields by section
   */
  private groupFieldsBySection(fields: FormField[]): Map<string, FormField[]> {
    const sections = new Map<string, FormField[]>();

    fields.forEach(field => {
      const section = field.section || 'General';
      if (!sections.has(section)) {
        sections.set(section, []);
      }
      sections.get(section)!.push(field);
    });

    return sections;
  }

  /**
   * Fetch dropdown options from API
   */
  async fetchDropdownOptions(apiEndpoint: string, params?: any): Promise<FieldOption[]> {
    try {
      const response = await this.apiClient.get(apiEndpoint, { params });

      if (Array.isArray(response.data)) {
        return response.data;
      } else if (response.data.data && Array.isArray(response.data.data)) {
        return response.data.data;
      } else {
        console.error('Unexpected dropdown data format:', response.data);
        return [];
      }
    } catch (error) {
      console.error('Error fetching dropdown options:', error);
      return [];
    }
  }

  /**
   * Validate field value based on configuration
   */
  validateField(field: FormField, value: any): { isValid: boolean; errorMessage?: string } {
    // Check required
    if (field.isMandatory && !value) {
      return {
        isValid: false,
        errorMessage: field.errorMessage || `${field.label} is required`
      };
    }

    // Check email format for email fields or fields with email validation rule
    if ((field.fieldType === 'email' || field.isEmail) && value) {
      const emailPattern = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
      if (!emailPattern.test(value)) {
        return {
          isValid: false,
          errorMessage: field.errorMessage || `Please enter a valid email address`
        };
      }
    }

    // Check URL format for URL fields
    if (field.fieldType === 'url' && value) {
      try {
        new URL(value);
      } catch {
        return {
          isValid: false,
          errorMessage: field.errorMessage || `Please enter a valid URL`
        };
      }
    }

    // Check phone format for phone/tel fields
    if ((field.fieldType === 'phone' || field.fieldType === 'tel') && value) {
      // Flexible phone pattern: allows +, spaces, dashes, parentheses, and digits
      // Minimum 10 digits required (excluding country code)
      const phonePattern = /^[\+]?[(]?[0-9]{1,4}[)]?[-\s\.]?[(]?[0-9]{1,4}[)]?[-\s\.]?[0-9]{1,9}$/;

      // Strip country code for digit count (country codes are 1-3 digits after +)
      let phoneForValidation = value;
      if (value.startsWith('+')) {
        // Remove + and country code (1-3 digits at the start)
        phoneForValidation = value.replace(/^\+\d{1,3}[\s-]?/, '');
      }
      const digitCount = phoneForValidation.replace(/\D/g, '').length;

      if (!phonePattern.test(value) || digitCount < 10) {
        return {
          isValid: false,
          errorMessage: field.errorMessage || `Please enter a valid phone number (minimum 10 digits)`
        };
      }
    }

    // Helper function to check if field is a phone/contact field
    const isPhoneField = (fieldName: string, fieldType: string): boolean => {
      const phoneFieldNames = ['contact_no', 'contactno', 'phone', 'mobile', 'telephone'];
      const fieldNameLower = fieldName.toLowerCase();
      return fieldType === 'phone' || fieldType === 'tel' ||
             phoneFieldNames.some(name => fieldNameLower.includes(name));
    };

    // Get value for validation (strip country code for phone fields)
    const getValueForLengthValidation = (val: string): string => {
      if (!val) return val;
      if (isPhoneField(field.fieldName, field.fieldType) && val.startsWith('+')) {
        // Known country dial codes (sorted by length descending to match longest first)
        const countryCodes = [
          '+63', '+91', '+1', '+44', '+61', '+65', '+60', '+66', '+62', '+81', '+86', '+82',
          '+971', '+966', '+974', '+973', '+968', '+965', '+964', '+852', '+853', '+886'
        ].sort((a, b) => b.length - a.length);

        // Find matching country code
        const matchedCode = countryCodes.find(code => val.startsWith(code));
        if (matchedCode) {
          return val.substring(matchedCode.length);
        }

        // Fallback: try to extract country code (2-4 digits after +)
        return val.replace(/^\+\d{1,3}/, '');
      }
      return val;
    };

    // Check pattern
    if (field.validationPattern && value) {
      // For phone fields, validate without country code
      const valueToValidate = isPhoneField(field.fieldName, field.fieldType)
        ? getValueForLengthValidation(value)
        : value;
      const pattern = new RegExp(field.validationPattern);
      if (!pattern.test(valueToValidate)) {
        return {
          isValid: false,
          errorMessage: field.errorMessage || `${field.label} format is invalid`
        };
      }
    }

    // Check length (for phone fields, ignore country code)
    const valueForLength = value ? getValueForLengthValidation(value) : '';
    if (field.minLength && value && valueForLength.length < field.minLength) {
      return {
        isValid: false,
        errorMessage: `${field.label} must be at least ${field.minLength} characters`
      };
    }

    if (field.maxLength && value && valueForLength.length > field.maxLength) {
      return {
        isValid: false,
        errorMessage: `${field.label} must not exceed ${field.maxLength} characters`
      };
    }

    // Check numeric range
    if (field.fieldType === 'number') {
      const numValue = Number(value);
      if (field.minValue !== undefined && numValue < field.minValue) {
        return {
          isValid: false,
          errorMessage: `${field.label} must be at least ${field.minValue}`
        };
      }
      if (field.maxValue !== undefined && numValue > field.maxValue) {
        return {
          isValid: false,
          errorMessage: `${field.label} must not exceed ${field.maxValue}`
        };
      }
    }

    return { isValid: true };
  }

  /**
   * Transform form data for submission
   */
  transformFormDataForSubmission(formData: any, fields: FormField[]): any {
    const transformed: any = {};

    fields.forEach(field => {
      const value = formData[field.fieldName];

      // Skip empty non-mandatory fields (but NOT number fields - they should be 0)
      if (!field.isMandatory && (value === undefined || value === null || value === '') && field.fieldType !== 'number') {
        return;
      }

      // Transform based on field type
      switch (field.fieldType) {
        case 'datetime':
          transformed[field.fieldName] = value ? new Date(value).toISOString() : null;
          break;
        case 'number':
          // Convert null/undefined/empty to 0 for number fields
          transformed[field.fieldName] = value !== null && value !== undefined && value !== '' ? Number(value) : 0;
          break;
        case 'checkbox':
          transformed[field.fieldName] = value === true || value === 'true' || value === '1';
          break;
        case 'multiselect':
          // For multiselect, ensure value is an array and join as comma-separated string
          if (Array.isArray(value)) {
            transformed[field.fieldName] = value.join(',');
          } else if (typeof value === 'string') {
            transformed[field.fieldName] = value;
          } else {
            transformed[field.fieldName] = '';
          }
          break;
        case 'location':
          // Handle location fields specially
          if (field.fieldName.toLowerCase().includes('latitude')) {
            transformed.Latitude = value;
          } else if (field.fieldName.toLowerCase().includes('longitude')) {
            transformed.Longitude = value;
          } else {
            transformed[field.fieldName] = value;
          }
          break;
        default:
          transformed[field.fieldName] = value;
      }
    });

    return transformed;
  }
}

export const formConfigService = new FormConfigService();
export default formConfigService;